/**
 * Unify ServiceRequest FHIR model.
 *
 * A service request represents a client's request for a therapeutic service
 * (individual therapy, couples therapy, IOP, etc.). It tracks the intake and
 * operational pipeline — eligibility, screening, matching, consultation — via
 * a single `meta.tag` (tracker stage). Each ServiceRequest is paired 1:1 with
 * an EpisodeOfCare that tracks the clinical care lifecycle.
 *
 * ┌──────────────────────────┬──────────────────────────────────────────────┐
 * │ FHIR field               │ Unify usage                                 │
 * ├──────────────────────────┼──────────────────────────────────────────────┤
 * │ status                   │ draft | active | on-hold | revoked          │
 * │                          │ (auto-derived from tracker stage)           │
 * │ intent                   │ Fixed: "order"                              │
 * │ priority                 │ routine | urgent                            │
 * │ code                     │ Therapy service type (therapy-service-type) │
 * │ category                 │ Fixed: service-request (service-req-cat CS) │
 * │ subject                  │ Reference<Patient> — the client             │
 * │ requester                │ Reference<Patient|Practitioner|Org> — who   │
 * │ performer                │ Reference<Organization>[] — practice bias   │
 * │ authoredOn               │ Submission date                             │
 * │ note                     │ Annotation[] — admin/provider notes         │
 * │ meta.tag (tracker-stage) │ Single source of truth for pipeline stage   │
 * ├──────────────────────────┼──────────────────────────────────────────────┤
 * │ EXTENSIONS               │                                             │
 * │ episode-of-care          │ valueReference → EpisodeOfCare (1:1)        │
 * │ session-format-preference│ valueString — in-person|virtual|hybrid|…    │
 * │ availability-preference  │ valueString (JSON array)                    │
 * │ areas-of-focus           │ valueString (JSON array, max 3)             │
 * │ provider-preferences     │ valueString (JSON array)                    │
 * │ reservation-session      │ valueString — intake session ID             │
 * │ reservation-expires-at   │ valueDateTime — intake reservation expiry   │
 * └──────────────────────────┴──────────────────────────────────────────────┘
 *
 * Tracker stage codes (system: tracker-stage):
 *   insurance-eligibility | waitlist | screening | matching | consultation |
 *   fulfilled | transfer | inactive | cancelled
 *
 * Therapy service type codes (system: therapy-service-type):
 *   individual | couples | family | group | child-adolescent |
 *   iop | medication-management | psych-testing
 */
import type { Extension, Reference, ServiceRequest } from '@medplum/fhirtypes';

import {
  AREAS_OF_FOCUS_EXT,
  AVAILABILITY_PREF_EXT,
  PROVIDER_PREFERENCES_EXT,
  RESERVATION_EXPIRES_AT_EXT,
  RESERVATION_SESSION_EXT,
  SERVICE_REQUEST_EOC_EXT,
  SESSION_FORMAT_PREF_EXT,
  THERAPY_SERVICE_TYPE_SYSTEM,
  TRACKER_STAGE_TAG_SYSTEM,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal unions
// ═══════════════════════════════════════════════════════════════════════

/**
 * FHIR `status` values — auto-derived from tracker stage.
 * Application code should read/write the tracker stage, not status.
 */
export type ServiceRequestStatus = 'draft' | 'active' | 'on-hold' | 'revoked';

/** Tracker stage codes — single source of truth for pipeline position. */
export type TrackerStage =
  | 'new-client'
  | 'needs-verification'
  | 'insurance-eligibility'
  | 'waitlist'
  | 'screening'
  | 'matching'
  | 'consultation'
  | 'fulfilled'
  | 'transfer'
  | 'inactive'
  | 'cancelled';

/** Therapy service type codes (shared with EpisodeOfCare) — admin-defined strings. */
export type SrServiceTypeCode = string;

/** Session format preference values. */
export type SessionFormatPreference = 'in-person' | 'virtual' | 'hybrid' | 'no-preference';

// ═══════════════════════════════════════════════════════════════════════
//  Standard field helpers
// ═══════════════════════════════════════════════════════════════════════

/** Therapy service type code from `code.coding` (what service was requested). */
export function getSrServiceTypeCode(resource: ServiceRequest): string | undefined {
  return resource.code?.coding?.find(
    (c) => c.system === THERAPY_SERVICE_TYPE_SYSTEM,
  )?.code;
}

/** Practice bias Organization reference from `performer[0]`. */
export function getPerformerRef(resource: ServiceRequest): Reference | undefined {
  return resource.performer?.[0];
}

// ═══════════════════════════════════════════════════════════════════════
//  Tracker stage (meta.tag)
// ═══════════════════════════════════════════════════════════════════════

/** Current tracker stage code from `meta.tag`. */
export function getTrackerStage(resource: ServiceRequest): TrackerStage | undefined {
  return resource.meta?.tag?.find(
    (t) => t.system === TRACKER_STAGE_TAG_SYSTEM,
  )?.code as TrackerStage | undefined;
}

/** Replace the tracker-stage tag. Creates `meta.tag` array if absent. */
export function setTrackerStage(resource: ServiceRequest, stage: TrackerStage): void {
  const others = (resource.meta?.tag ?? []).filter(
    (t) => t.system !== TRACKER_STAGE_TAG_SYSTEM,
  );
  resource.meta = {
    ...resource.meta,
    tag: [...others, { system: TRACKER_STAGE_TAG_SYSTEM, code: stage }],
  };
}

/** Remove the tracker-stage tag (e.g. when returning to draft). */
export function clearTrackerStage(resource: ServiceRequest): void {
  const others = (resource.meta?.tag ?? []).filter(
    (t) => t.system !== TRACKER_STAGE_TAG_SYSTEM,
  );
  resource.meta = { ...resource.meta, tag: others.length ? others : undefined };
}

// ═══════════════════════════════════════════════════════════════════════
//  Extension getters
// ═══════════════════════════════════════════════════════════════════════

/** The 1:1 linked EpisodeOfCare reference. */
export function getEpisodeOfCareRef(resource: ServiceRequest): Reference | undefined {
  return resource.extension?.find((e) => e.url === SERVICE_REQUEST_EOC_EXT)?.valueReference;
}

/** Session format preference (in-person, virtual, hybrid, no-preference). */
export function getSessionFormatPreference(resource: ServiceRequest): SessionFormatPreference | undefined {
  return resource.extension?.find((e) => e.url === SESSION_FORMAT_PREF_EXT)
    ?.valueString as SessionFormatPreference | undefined;
}

/** Availability preference (JSON-encoded array of time-of-week slots). */
export function getAvailabilityPreference(resource: ServiceRequest): string[] | undefined {
  const raw = resource.extension?.find((e) => e.url === AVAILABILITY_PREF_EXT)?.valueString;
  return raw ? (JSON.parse(raw) as string[]) : undefined;
}

/** Areas of focus (JSON-encoded array, max 3 topics). */
export function getAreasOfFocus(resource: ServiceRequest): string[] | undefined {
  const raw = resource.extension?.find((e) => e.url === AREAS_OF_FOCUS_EXT)?.valueString;
  return raw ? (JSON.parse(raw) as string[]) : undefined;
}

/** Provider preferences (JSON-encoded array of demographic/style prefs). */
export function getProviderPreferences(resource: ServiceRequest): string[] | undefined {
  const raw = resource.extension?.find((e) => e.url === PROVIDER_PREFERENCES_EXT)?.valueString;
  return raw ? (JSON.parse(raw) as string[]) : undefined;
}

/** Intake reservation session ID. */
export function getSrReservationSession(resource: ServiceRequest): string | undefined {
  return resource.extension?.find((e) => e.url === RESERVATION_SESSION_EXT)?.valueString;
}

/** Intake reservation expiry timestamp. */
export function getSrReservationExpiresAt(resource: ServiceRequest): string | undefined {
  return resource.extension?.find((e) => e.url === RESERVATION_EXPIRES_AT_EXT)?.valueDateTime;
}

// ═══════════════════════════════════════════════════════════════════════
//  Extension setters (mutate in place)
// ═══════════════════════════════════════════════════════════════════════

/** Set or remove the linked EpisodeOfCare reference. */
export function setEpisodeOfCareRef(resource: ServiceRequest, ref: Reference | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== SERVICE_REQUEST_EOC_EXT);
  resource.extension = ref
    ? [...others, { url: SERVICE_REQUEST_EOC_EXT, valueReference: ref }]
    : others.length ? others : undefined;
}

/** Set or remove the session format preference. */
export function setSessionFormatPreference(
  resource: ServiceRequest,
  value: SessionFormatPreference | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== SESSION_FORMAT_PREF_EXT);
  resource.extension = value
    ? [...others, { url: SESSION_FORMAT_PREF_EXT, valueString: value }]
    : others.length ? others : undefined;
}

/** Set or remove the availability preference (serialised as JSON array). */
export function setAvailabilityPreference(
  resource: ServiceRequest,
  slots: string[] | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== AVAILABILITY_PREF_EXT);
  resource.extension = slots?.length
    ? [...others, { url: AVAILABILITY_PREF_EXT, valueString: JSON.stringify(slots) }]
    : others.length ? others : undefined;
}

/** Set or remove the areas of focus (serialised as JSON array, max 3). */
export function setAreasOfFocus(resource: ServiceRequest, topics: string[] | undefined): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== AREAS_OF_FOCUS_EXT);
  resource.extension = topics?.length
    ? [...others, { url: AREAS_OF_FOCUS_EXT, valueString: JSON.stringify(topics) }]
    : others.length ? others : undefined;
}

/** Set or remove provider preferences (serialised as JSON array). */
export function setProviderPreferences(
  resource: ServiceRequest,
  prefs: string[] | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== PROVIDER_PREFERENCES_EXT);
  resource.extension = prefs?.length
    ? [...others, { url: PROVIDER_PREFERENCES_EXT, valueString: JSON.stringify(prefs) }]
    : others.length ? others : undefined;
}

/** Set or remove the reservation-session extension (intake flow). */
export function setSrReservationSession(
  resource: ServiceRequest,
  sessionId: string | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RESERVATION_SESSION_EXT);
  resource.extension = sessionId
    ? [...others, { url: RESERVATION_SESSION_EXT, valueString: sessionId }]
    : others.length ? others : undefined;
}

/** Set or remove the reservation-expires-at extension (intake flow). */
export function setSrReservationExpiresAt(
  resource: ServiceRequest,
  dateTime: string | undefined,
): void {
  const others = (resource.extension ?? []).filter((e) => e.url !== RESERVATION_EXPIRES_AT_EXT);
  resource.extension = dateTime
    ? [...others, { url: RESERVATION_EXPIRES_AT_EXT, valueDateTime: dateTime }]
    : others.length ? others : undefined;
}

/** Remove all reservation-related extensions (called on promotion). */
export function clearSrReservationExtensions(resource: ServiceRequest): void {
  resource.extension = (resource.extension ?? []).filter(
    (e) => e.url !== RESERVATION_SESSION_EXT && e.url !== RESERVATION_EXPIRES_AT_EXT,
  ) as Extension[] | undefined;
  if (resource.extension?.length === 0) resource.extension = undefined;
}
